import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Menu, X, ChevronLeft, ChevronRight, Star, MapPin, Phone, Clock } from 'lucide-react';

// ── Media CDN ─────────────────────────────────────────────────────────────────
const M = 'https://plada-media.s3.us-east-1.amazonaws.com/landing-assets/centro-deportivo-lmt';

// ── Brand ─────────────────────────────────────────────────────────────────────
const LOGO = 'https://plada-uploads.s3.us-east-1.amazonaws.com/tenant-logos/logo-cdp-2026-02-02T17-00-31-824Z.png';
const FAVICON_LIGHT = 'https://plada-uploads.s3.us-east-1.amazonaws.com/tenant-logos/lmt-favicon-e7cb355a-6e9a-44bd-84f4-fe81afab34df.png';
const FAVICON_DARK = 'https://plada-uploads.s3.us-east-1.amazonaws.com/tenant-logos/lmt-favicon-dark-92f9d13f-e33c-46df-9372-7e78ae918e8c.png';
const WHATSAPP_URL = 'https://wa.me/573155502034?text=Hola%2C%20quiero%20informaci%C3%B3n%20sobre%20Centro%20Deportivo%20Luz%20Mery%20Trist%C3%A1n';

// ── Images ────────────────────────────────────────────────────────────────────
const IMG = {
  i2:  'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/3524d820-716a-4c54-8667-15cc3bfd1fb6-590767301_18440438200103185_4436729330208533622_n.jpg',
  i4:  'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/5eeb9e64-1f8f-42a5-b52f-1ed291853ecd-591165460_18440556784103185_7603150527634580195_n.jpg',
  i9:  'https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/f41bb54e-6341-4caa-9d10-5db9c14ee0a8-630110635_909930831404779_2087257616712377500_n.jpg',
};

const HERO_IMG   = 'https://images.unsplash.com/photo-1710993115028-74742698a22b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxhdGhsZXRlJTIwc3ByaW50aW5nJTIwc3RhZGl1bSUyMGRyYW1hdGljJTIwbGlnaHRpbmd8ZW58MXx8fHwxNzczMDg5MTc3fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral';

const GALLERIES: Partial<Record<View, string[]>> = {
  natacion: [
    `${M}/natacion-1.jpg`,
    `${M}/natacion-2.png`,
    `${M}/natacion-3.png`,
    `${M}/natacion-4.png`,
    `${M}/natacion-5.png`,
    `${M}/natacion-6.png`,
  ],
  patinaje: [
    `${M}/patinaje-1.jpg`,
    `${M}/patinaje-2.jpg`,
    `${M}/patinaje-3.jpg`,
    `${M}/patinaje-4.png`,
    `${M}/patinaje-5.png`,
    `${M}/patinaje-6.png`,
  ],
  beauty: [
    `${M}/beauty-1.mp4`,
    `${M}/beauty-2.mp4`,
    `${M}/beauty-3.mp4`,
    `${M}/beauty-4.mp4`,
    `${M}/beauty-5.mp4`,
    `${M}/beauty-6.mp4`,
  ],
};

function isVideo(src: string) {
  return src.endsWith('.mp4') || src.endsWith('.webm') || src.endsWith('.mov');
}

// First 8 items = activity cards (have desc). Rest = extra gallery.
const SC_GALLERY: { src: string; label: string; desc?: string }[] = [
  { src: `${M}/sc-7.jpg`,  label: 'Natación',              desc: 'Clases en piscina semiolímpica con instructores certificados.' },
  { src: `${M}/sc-8.jpg`,  label: 'Patinaje',               desc: 'Pista cubierta con equipo completo de protección incluido.' },
  { src: `${M}/sc-12.jpg`, label: 'Artes Marciales',        desc: 'Disciplina, respeto y movimiento con sensei especializado.' },
  { src: `${M}/sc-4.jpg`,  label: 'Pintura & Manualidades', desc: 'Creatividad sin límites: globos, pintura y arte libre.' },
  { src: `${M}/sc-6.jpg`,  label: 'Equitación',             desc: 'Paseos a caballo guiados en instalaciones seguras.' },
  { src: `${M}/sc-3.jpg`,  label: 'Actividades Acuáticas',  desc: 'Water slide, piscina de espuma y juegos con agua.' },
  { src: `${M}/sc-1.jpg`,  label: 'Pádel',                  desc: 'Cancha profesional con clase introductoria incluida.' },
  { src: `${M}/sc-11.jpg`, label: 'Nutrición & Snacks',     desc: 'Frutas, picnic saludable y hábitos que divierten.' },
  // extra gallery
  { src: `${M}/sc-9.jpg`,  label: 'Patinaje' },
  { src: `${M}/sc-2.jpg`,  label: 'Artes Marciales' },
  { src: `${M}/sc-13.png`, label: 'Artes Marciales' },
  { src: `${M}/sc-10.jpg`, label: 'Manualidades' },
  { src: `${M}/sc-5.jpg`,  label: 'Picnic' },
  { src: `${M}/sc-v1.mov`, label: 'Campamento' },
  { src: `${M}/sc-v2.mov`, label: 'Campamento' },
  { src: `${M}/sc-v3.mov`, label: 'Campamento' },
  { src: `${M}/sc-v4.mov`, label: 'Campamento' },
];
const IMG_SKATE  = 'https://images.unsplash.com/photo-1765728736092-9bc4d2aa4f39?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxyb2xsZXIlMjBza2F0aW5nJTIwc3BvcnQlMjBmdW58ZW58MXx8fHwxNzczMDg5ODg2fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral';
const IMG_DANCE  = 'https://images.unsplash.com/photo-1779126304909-791eee072e42?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxraWRzJTIwZGFuY2UlMjBzdHVkaW8lMjBoaXAlMjBob3AlMjBwZXJmb3JtYW5jZXxlbnwxfHx8fDE3Nzk5MTI4NjJ8MA&ixlib=rb-4.1.0&q=80&w=1080';
const IMG_WELL   = 'https://images.unsplash.com/photo-1630226040750-d934f017f0e4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx3ZWxsbmVzcyUyMHBoeXNpb3RoZXJhcHklMjBzcGElMjBjZW50ZXJ8ZW58MXx8fHwxNzc5OTEyODYzfDA&ixlib=rb-4.1.0&q=80&w=1080';
const IMG_BEAUTY = 'https://images.unsplash.com/photo-1634449571010-02389ed0f9b0?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxoYWlyJTIwc2Fsb24lMjBiZWF1dHklMjBsaWZlc3R5bGUlMjBtb2Rlcm58ZW58MXx8fHwxNzc5OTEyODY0fDA&ixlib=rb-4.1.0&q=80&w=1080';

// ── Routing ───────────────────────────────────────────────────────────────────
type View = 'home' | 'natacion' | 'patinaje' | 'kidultz' | 'wellness' | 'beauty' | 'lmtristan' | 'summercamp';
const VALID_VIEWS: View[] = ['natacion', 'patinaje', 'kidultz', 'wellness', 'beauty', 'lmtristan', 'summercamp'];

function getBase(): string {
  const p = typeof window !== 'undefined' && window.location?.pathname;
  if (!p) return '';
  return p.startsWith('/page/centro-deportivo-lmt') ? '/page/centro-deportivo-lmt' : '';
}

function pathToView(): View {
  const p = typeof window !== 'undefined' && window.location?.pathname;
  if (!p) return 'home';
  const base = getBase();
  const sub = p.slice(base.length).replace(/^\/+|\/+$/g, '');
  return (VALID_VIEWS.includes(sub as View) ? sub : 'home') as View;
}

function scrollTop() {
  (document.querySelector('main') as HTMLElement | null)?.scrollTo({ top: 0 });
  window.scrollTo(0, 0);
}

function useNav() {
  const [view, setView] = useState<View>(pathToView);
  const go = useCallback((v: View) => {
    const base = getBase();
    const url = v === 'home' ? (base + '/') : `${base}/${v}`;
    window.history.pushState({ v }, '', url);
    setView(v);
    scrollTop();
  }, []);
  useEffect(() => {
    const onPop = (e: PopStateEvent) => {
      setView((e.state?.v as View) || pathToView());
      scrollTop();
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  return { view, go };
}

// ── Service catalog ───────────────────────────────────────────────────────────
interface ServiceDef {
  id: View;
  title: string;
  subtitle: string;
  body: string;
  image: string;
  tags: string[];
}

const CATALOG: ServiceDef[] = [
  {
    id: 'natacion',
    title: 'Natación',
    subtitle: 'Piscina semiolímpica',
    body: 'Clases para bebés, niños, adultos y semilleros competitivos. Instructores certificados con más de 30 años formando campeones en nuestra piscina semiolímpica.',
    image: IMG.i4,
    tags: ['Bebés', 'Niños', 'Adultos', 'Semilleros', 'Personalizado'],
  },
  {
    id: 'patinaje',
    title: 'Patinaje',
    subtitle: 'Pista profesional',
    body: 'Tres modalidades: tapete, artístico y personalizado. Para niños desde los 3 años y adultos de todos los niveles en nuestra pista cubierta.',
    image: IMG_SKATE,
    tags: ['Niños', 'Adultos', 'Artístico', 'Tapete', 'Personalizado'],
  },
  {
    id: 'kidultz',
    title: 'Kidultz Studio',
    subtitle: 'Academia de danza',
    body: 'Formación artística y competencia en Hip Hop, Jazz, Salsa, Ballet y Acrobacia. Para niños y jóvenes que sueñan con el escenario.',
    image: IMG_DANCE,
    tags: ['Hip Hop', 'Jazz', 'Salsa', 'Ballet', 'Acrobacia'],
  },
  {
    id: 'wellness',
    title: 'Wellness',
    subtitle: 'Bienestar integral',
    body: 'Fisioterapia deportiva, asesoría nutricional y apoyo psicológico. Todo lo que tu cuerpo y mente necesitan para rendir al máximo.',
    image: IMG_WELL,
    tags: ['Fisioterapia', 'Nutrición', 'Psicología'],
  },
  {
    id: 'beauty',
    title: 'Beauty & Lifestyle',
    subtitle: 'Estilo de vida',
    body: 'Peluquería especializada, Burbujitas café y clínica de ropa deportiva. Porque el deporte también es estilo.',
    image: IMG_BEAUTY,
    tags: ['Peluquería', 'Burbujitas', 'Ropa deportiva'],
  },
];

// ── Pricing data ──────────────────────────────────────────────────────────────
interface Tier { name: string; price: string; }
interface PlanGroup { title: string; matricula?: string; tiers: Tier[]; schedule?: string[]; }

const NATACION_GROUPS: PlanGroup[] = [
  {
    title: 'Niños y Bebés',
    matricula: '$99.200',
    tiers: [
      { name: 'Clase ocasional', price: '$72.600' },
      { name: '4 clases', price: '$272.600' },
      { name: '8 clases', price: '$420.600' },
      { name: '12 clases', price: '$624.400' },
      { name: 'Semilleros', price: '$406.300' },
    ],
    schedule: [
      'Lun – Vie: 7:00 – 8:00 p.m.',
      'Lun, Mié y Vie: 12:30 – 1:30 p.m.',
      'Mar – Vie: 7:30 – 8:30 a.m.',
      'Sábados: 7:30 – 8:30 a.m.',
    ],
  },
  {
    title: 'Adultos',
    matricula: '$71.900',
    tiers: [
      { name: 'Clase ocasional', price: '$72.600' },
      { name: '4 clases', price: '$159.700' },
      { name: '8 clases', price: '$297.300' },
      { name: '12 clases', price: '$389.400' },
      { name: '20 clases', price: '$660.700' },
    ],
  },
  {
    title: 'Gym + Natación',
    tiers: [
      { name: '1×/semana', price: '$381.000' },
      { name: '2×/semana', price: '$448.000' },
      { name: 'Full', price: '$500.000' },
    ],
  },
  {
    title: 'Personalizado',
    tiers: [
      { name: '1 clase', price: '$125.100' },
      { name: 'Combo 4 clases', price: '$435.500' },
      { name: 'Combo 8 clases', price: '$869.600' },
      { name: 'Combo 12 clases', price: '$1.181.000' },
    ],
  },
];

const PATINAJE_GROUPS: PlanGroup[] = [
  {
    title: 'Niños',
    matricula: '$99.200',
    tiers: [
      { name: 'Clase ocasional', price: '$59.900' },
      { name: '4 clases', price: '$190.400' },
      { name: '8 clases', price: '$274.200' },
      { name: '12 clases', price: '$335.200' },
      { name: 'Especialización', price: '$375.100' },
    ],
    schedule: [
      'Lun – Vie: 4:00 – 5:00 p.m. y 5:00 – 6:00 p.m.',
      'Sábados: 9:00 a.m. – 12:00 m.',
    ],
  },
  {
    title: 'Artístico',
    matricula: '$85.500',
    tiers: [
      { name: 'Clase ocasional', price: '$57.000' },
      { name: '4 clases/mes', price: '$241.400' },
      { name: '8 clases/mes', price: '$260.000' },
      { name: '12 clases/mes', price: '$296.400' },
    ],
    schedule: ['Mar, Mié y Vie: 5:30 – 7:00 p.m.'],
  },
  {
    title: 'Adultos',
    matricula: '$71.900',
    tiers: [
      { name: 'Clase ocasional', price: '$59.900' },
      { name: '4 clases', price: '$144.800' },
      { name: '8 clases', price: '$209.800' },
      { name: '12 clases/mes', price: '$261.100' },
      { name: 'Todos los días', price: '$291.900' },
    ],
    schedule: ['Viernes: 6:30 – 8:00 p.m.', 'Sábados: 10:00 – 11:00 a.m.'],
  },
  {
    title: 'Gym + Patinaje',
    tiers: [
      { name: '1×/semana', price: '$365.500' },
      { name: '2×/semana', price: '$404.000' },
      { name: 'Full', price: '$423.500' },
    ],
  },
  {
    title: 'Personalizado',
    tiers: [
      { name: '1 clase', price: '$99.800' },
      { name: 'Combo 4 clases', price: '$359.100' },
      { name: 'Combo 8 clases', price: '$682.300' },
      { name: 'Combo 12 clases', price: '$897.800' },
    ],
  },
];

// ── Navbar ────────────────────────────────────────────────────────────────────
function Navbar({ view, go }: { view: View; go: (v: View) => void }) {
  const [open, setOpen] = useState(false);
  const isHome = view === 'home';
  const current = CATALOG.find((s) => s.id === view);
  const subLabel = view === 'lmtristan' ? 'Luz Mery Tristán' : view === 'summercamp' ? 'Kids Summer Camp' : current?.title;

  return (
    <nav className="sticky top-0 z-50 bg-white border-b border-gray-100">
      <div className="max-w-[1920px] mx-auto px-5 lg:px-10 h-[60px] flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 shrink-0">
          {!isHome && (
            <button
              onClick={() => go('home')}
              className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-[#f0f6ff] text-[#001489] transition-colors"
              aria-label="Volver"
            >
              <ChevronLeft size={20} />
            </button>
          )}
          <button onClick={() => go('home')}>
            <img src={LOGO} alt="Centro Deportivo LMT" className="h-8 w-auto" />
          </button>
          {!isHome && subLabel && (
            <span className="hidden sm:block text-gray-400 ml-1" style={{ fontSize: '13px' }}>
              / <span className="text-[#001489] font-semibold">{subLabel}</span>
            </span>
          )}
        </div>

        {isHome && (
          <div className="hidden lg:flex items-center gap-6">
            {CATALOG.map((s) => (
              <button
                key={s.id}
                onClick={() => go(s.id)}
                className="text-[#001489] hover:text-[#009edd] transition-colors relative group"
                style={{ fontSize: '14px', fontWeight: 500 }}
              >
                {s.title}
                <span className="absolute -bottom-0.5 left-0 w-0 group-hover:w-full h-[2px] bg-[#fd3eb5] transition-all duration-200" />
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-3">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:block text-white px-4 py-2 rounded-full transition-colors hover:bg-[#009edd]"
            style={{ fontSize: '13px', fontWeight: 600, background: '#001489' }}
          >
            Únete
          </a>
          <button
            className="lg:hidden text-[#001489]"
            onClick={() => setOpen(!open)}
            aria-label="Menú"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-gray-100 absolute top-full left-0 right-0 shadow-xl z-50">
          <div className="px-5 py-4 space-y-1">
            {CATALOG.map((s) => (
              <button
                key={s.id}
                onClick={() => { go(s.id); setOpen(false); }}
                className="flex items-center gap-3 w-full py-3 border-b border-gray-50 text-left"
              >
                <img src={s.image} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                <div>
                  <p className="text-[#001489]" style={{ fontSize: '15px', fontWeight: 600 }}>{s.title}</p>
                  <p className="text-gray-400" style={{ fontSize: '12px' }}>{s.subtitle}</p>
                </div>
              </button>
            ))}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center text-white py-3 rounded-full mt-3"
              style={{ background: '#001489', fontSize: '15px', fontWeight: 600 }}
            >
              Únete ahora
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}

// ── Home › Hero ───────────────────────────────────────────────────────────────
function HeroSection({ go }: { go: (v: View) => void }) {
  return (
    <section className="relative w-full" style={{ height: 'calc(100vh - 60px)', minHeight: '520px' }}>
      <img src={HERO_IMG} alt="Atleta en acción" className="w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(0,20,137,0.78) 0%, rgba(0,20,137,0.42) 100%)' }} />
      <div className="absolute inset-0 flex flex-col justify-end px-8 lg:px-16 pb-16 lg:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl"
        >
          <p className="text-[#009edd] mb-3" style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase' }}>
            Cali, Colombia · Más de 30 años
          </p>
          <h1 className="text-white mb-4" style={{ fontSize: 'clamp(52px, 8vw, 96px)', fontWeight: 900, lineHeight: 0.88, letterSpacing: '-0.03em', textTransform: 'uppercase', fontStyle: 'italic' }}>
            Muévete<br />en Familia.
          </h1>
          <p className="text-white/70 mb-8 max-w-md" style={{ fontSize: 'clamp(15px, 1.8vw, 18px)', lineHeight: 1.65 }}>
            Natación, patinaje, danza, wellness y más — para niños, jóvenes y adultos en un solo lugar.
          </p>
          <div className="flex flex-wrap gap-5">
            <button
              onClick={() => go('natacion')}
              className="text-white border-b-2 border-[#fd3eb5] pb-0.5 hover:text-[#fd3eb5] transition-colors"
              style={{ fontSize: '15px', fontWeight: 700 }}
            >
              Ver programas →
            </button>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white border-b-2 border-[#009edd] pb-0.5 hover:text-[#009edd] transition-colors"
              style={{ fontSize: '15px', fontWeight: 700 }}
            >
              Clase gratis
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ── Home › Stats ──────────────────────────────────────────────────────────────
const STATS = [
  { value: '30+', label: 'Años' },
  { value: '5K+', label: 'Familias' },
  { value: '5', label: 'Disciplinas' },
  { value: '200+', label: 'Medallistas' },
];

function StatsBar() {
  return (
    <div className="bg-[#001489]">
      <div className="max-w-[1920px] mx-auto px-6 lg:px-10 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
        {STATS.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.07 }}
            className="text-center"
          >
            <p className="text-white" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 900, letterSpacing: '-0.03em' }}>
              {s.value}
            </p>
            <p className="text-[#009edd]" style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
              {s.label}
            </p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ── Home › Services grid ──────────────────────────────────────────────────────
function ServicesGrid({ go }: { go: (v: View) => void }) {
  return (
    <section className="bg-white py-16 lg:py-20">
      <div className="max-w-[1920px] mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-10"
        >
          <p className="text-[#009edd] mb-2" style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
            Lo que ofrecemos
          </p>
          <h2 className="text-[#001489]" style={{ fontSize: 'clamp(1.6rem, 3vw, 2.4rem)', fontWeight: 800, letterSpacing: '-0.02em' }}>
            Un universo deportivo completo
          </h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {CATALOG.map((s, i) => (
            <motion.button
              key={s.id}
              onClick={() => go(s.id)}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.07 }}
              className="group relative rounded-xl overflow-hidden text-left"
              style={{ aspectRatio: '4/3' }}
            >
              <img
                src={s.image}
                alt={s.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div
                className="absolute inset-0 transition-opacity duration-300"
                style={{ background: 'linear-gradient(to top, rgba(0,20,137,0.88) 0%, rgba(0,20,137,0.3) 55%, transparent 100%)' }}
              />
              <div className="absolute inset-0 flex flex-col justify-end p-6">
                <p className="text-[#009edd] mb-1" style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                  {s.subtitle}
                </p>
                <h3 className="text-white mb-2" style={{ fontSize: 'clamp(20px, 2.5vw, 26px)', fontWeight: 800, letterSpacing: '-0.01em', textTransform: 'uppercase' }}>
                  {s.title}
                </h3>
                <p className="text-white/60 mb-3" style={{ fontSize: '13px', lineHeight: 1.5 }}>
                  {s.tags.slice(0, 3).join(' · ')}
                </p>
                <span
                  className="text-[#fd3eb5] opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  style={{ fontSize: '13px', fontWeight: 700 }}
                >
                  Ver detalles →
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Home › About ──────────────────────────────────────────────────────────────
function AboutSection() {
  return (
    <section className="bg-[#f0f6ff]">
      <div className="max-w-[1920px] mx-auto px-6 lg:px-10 py-16 lg:py-20 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative overflow-hidden rounded-xl"
          style={{ aspectRatio: '4/3' }}
        >
          <img src={IMG.i2} alt="Familias en el centro deportivo" className="w-full h-full object-cover" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          <p className="text-[#009edd] mb-3" style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
            Nuestra historia
          </p>
          <h2 className="text-[#001489] mb-5" style={{ fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', fontWeight: 800, letterSpacing: '-0.02em', textTransform: 'uppercase', lineHeight: 1.05 }}>
            Más de 30 años<br />formando campeones.
          </h2>
          <p className="text-gray-600 mb-4" style={{ fontSize: '15px', lineHeight: 1.75 }}>
            Inspirados en la campeona olímpica Luz Mery Tristán, somos el hogar deportivo de miles de familias caleñas. Niños, jóvenes y adultos entrenando juntos en un ambiente profesional, seguro y divertido.
          </p>
          <p className="text-gray-600 mb-7" style={{ fontSize: '15px', lineHeight: 1.75 }}>
            Natación, patinaje, danza, wellness y beauty — todo bajo un mismo techo en el corazón de Cali.
          </p>
          <div className="flex flex-wrap gap-5">
            <div className="flex items-center gap-2">
              <MapPin size={15} className="text-[#009edd] shrink-0" />
              <span className="text-[#001489]" style={{ fontSize: '14px' }}>Cl. 18 #168-5149, Cali</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={15} className="text-[#009edd] shrink-0" />
              <span className="text-[#001489]" style={{ fontSize: '14px' }}>Lun – Sáb · 6am – 9pm</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ── Home › Luz Mery Tristán ───────────────────────────────────────────────────
const LMT_TIMELINE = [
  { year: '1963', text: 'Nació en Pereira. A los 14 años descubrió su pasión: el patinaje.' },
  { year: '1979', text: '1er Campeonato Nacional Femenino de Patinaje.' },
  { year: '1986', text: 'Participó en el Tour de Francia.' },
  { year: '1987', text: 'Campeona Panamericana de Patinaje en Indianapolis.' },
  { year: '1988', text: 'Medalla de Bronce en el Mundial de Italia.' },
  { year: '1990', text: 'Campeona Mundial de Patinaje en Bello, Antioquia, Colombia.' },
  { year: '1997', text: 'Fundó la Escuela de Patinaje Luz Mery Tristán en Pance, Cali.' },
];

// ── Home › Luz Mery teaser (compact strip) ────────────────────────────────────
function LuzMeryTeaser({ go }: { go: (v: View) => void }) {
  return (
    <motion.section
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
      className="relative w-full overflow-hidden cursor-pointer group"
      style={{ height: '300px' }}
      onClick={() => go('lmtristan')}
    >
      <img
        src={`${M}/lmt-banner.png`}
        alt="Luz Mery Tristán"
        className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
      />
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(90deg, rgba(0,20,137,0.88) 0%, rgba(0,20,137,0.6) 40%, rgba(0,20,137,0.15) 75%, transparent 100%)' }}
      />
      <div className="absolute inset-0 flex items-center px-8 lg:px-16">
        <div className="max-w-xl">
          <p className="text-[#fd3eb5]" style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.3em', textTransform: 'uppercase', marginBottom: 14 }}>
            La fundadora
          </p>
          <h2 className="text-white" style={{ fontSize: 'clamp(44px, 7vw, 96px)', fontWeight: 900, letterSpacing: '-0.04em', textTransform: 'uppercase', lineHeight: 0.85 }}>
            Luz Mery<br />
            <span className="text-[#fd3eb5]">Tristán</span>
          </h2>
          <p className="text-white/50 mt-3 mb-6" style={{ fontSize: '13px', letterSpacing: '0.08em' }}>
            Campeona mundial · Pionera · Fundadora
          </p>
          <span
            className="inline-flex items-center gap-2 text-white border-b-2 border-[#fd3eb5] pb-0.5 group-hover:text-[#fd3eb5] transition-colors"
            style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.05em' }}
          >
            Conoce su historia →
          </span>
        </div>
      </div>
    </motion.section>
  );
}

// ── LMT full page ─────────────────────────────────────────────────────────────
function LuzMeryView({ go }: { go: (v: View) => void }) {
  return (
    <>
      {/* Hero */}
      <section className="relative w-full overflow-hidden" style={{ height: 'clamp(420px, 55vw, 640px)' }}>
        <img
          src={`${M}/lmt-banner.png`}
          alt="Luz Mery Tristán"
          className="w-full h-full object-cover object-center"
        />
        {/* Subtle left-to-right vignette so text is readable without killing the photo */}
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(90deg, rgba(0,20,137,0.72) 0%, rgba(0,20,137,0.42) 38%, rgba(0,20,137,0.08) 70%, transparent 100%)' }}
        />
        {/* Thin bottom fade to white */}
        <div
          className="absolute bottom-0 left-0 right-0"
          style={{ height: '120px', background: 'linear-gradient(to bottom, transparent, rgba(255,255,255,0.35))' }}
        />
        <div className="absolute inset-0 flex items-center px-8 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
            <p className="text-[#fd3eb5] mb-3" style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.3em', textTransform: 'uppercase' }}>
              1 de abril, 1963 — Pereira, Colombia
            </p>
            <h1 className="text-white" style={{ fontSize: 'clamp(52px, 9vw, 120px)', fontWeight: 900, letterSpacing: '-0.04em', textTransform: 'uppercase', lineHeight: 0.84, textShadow: '0 2px 24px rgba(0,20,137,0.3)' }}>
              Luz<br />Mery<br /><span className="text-[#fd3eb5]">Tristán</span>
            </h1>
            <p className="text-white/80 mt-5" style={{ fontSize: 'clamp(13px, 1.5vw, 17px)', maxWidth: '360px', lineHeight: 1.65, textShadow: '0 1px 8px rgba(0,0,0,0.3)' }}>
              Seré recordada como la campeona del mundo.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-white">
        <div className="max-w-5xl mx-auto px-8 lg:px-16 py-16 lg:py-24">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-[#009edd] mb-3" style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase' }}>Su historia</p>
              <p className="text-[#001489]" style={{ fontSize: 'clamp(22px, 3vw, 34px)', fontWeight: 800, lineHeight: 1.2, letterSpacing: '-0.02em' }}>
                Mi experiencia es un legado que inspira.
              </p>
            </div>
            <div className="space-y-4">
              <p className="text-gray-600" style={{ fontSize: '15px', lineHeight: 1.8 }}>
                Luz Mery Tristán llegó al mundo en Pereira el 1 de abril. A los 14 años abrazó el patinaje como su pasión y tomó la audaz decisión de transformar su vida, el deporte que la llevaría a conquistar sus sueños.
              </p>
              <p className="text-gray-600" style={{ fontSize: '15px', lineHeight: 1.8 }}>
                Luego del retiro del patinaje se dedicó a la crianza de sus hijos y fundó su escuela en Pance. Hoy, miles de niños y familias caleñas continúan su legado.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Timeline — Nike-style: year as giant number */}
      <section className="bg-[#f8f9fc]">
        <div className="max-w-5xl mx-auto px-8 lg:px-16 py-16 lg:py-20">
          <p className="text-[#001489] mb-14" style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase' }}>
            Cronología
          </p>
          <div className="space-y-0 divide-y divide-gray-100">
            {LMT_TIMELINE.map((item, i) => (
              <motion.div
                key={item.year}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="flex items-baseline gap-8 lg:gap-16 py-8"
              >
                <span
                  className="shrink-0 text-[#001489]/10"
                  style={{ fontSize: 'clamp(44px, 7vw, 80px)', fontWeight: 900, letterSpacing: '-0.05em', lineHeight: 1, minWidth: '7rem', textAlign: 'right' }}
                >
                  {item.year}
                </span>
                <p className="text-[#001489]" style={{ fontSize: 'clamp(15px, 1.8vw, 20px)', fontWeight: 600, lineHeight: 1.4 }}>
                  {item.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quotes */}
      <section className="bg-[#001489]">
        <div className="max-w-5xl mx-auto px-8 lg:px-16 py-16 lg:py-24 grid lg:grid-cols-2 gap-10 lg:gap-16">
          {[
            'Si te caes, te levantas y sigues.',
            'Para atrás, ni para coger impulso.',
            'El valor de un campeón es la fuerza con la que se levanta.',
            'Mi experiencia es un legado que inspira.',
          ].map((q, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className={i % 2 === 0 ? 'text-white' : 'text-[#fd3eb5]'}
              style={{ fontSize: 'clamp(18px, 2.5vw, 26px)', fontWeight: 800, lineHeight: 1.25, fontStyle: 'italic', letterSpacing: '-0.01em' }}
            >
              &ldquo;{q}&rdquo;
            </motion.p>
          ))}
        </div>
      </section>

      {/* CTA back to home */}
      <section className="bg-white py-16 px-8 lg:px-16 text-center">
        <p className="text-gray-400 mb-6" style={{ fontSize: '13px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          Sigue su legado
        </p>
        <button
          onClick={() => go('patinaje')}
          className="inline-block text-white px-10 py-4 hover:bg-[#009edd] transition-colors"
          style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', background: '#001489' }}
        >
          Ver programa de Patinaje →
        </button>
      </section>
    </>
  );
}

// ── Home › Events (Nike-style) ────────────────────────────────────────────────
function EventsSection({ go }: { go: (v: View) => void }) {
  return (
    <section className="bg-white">
      <div className="max-w-[1920px] mx-auto px-6 lg:px-10 pt-14 pb-4 flex items-center justify-between">
        <p className="text-[#001489]" style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase' }}>
          Noticias &amp; Eventos
        </p>
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#001489] border-b border-[#001489] pb-0.5 hover:text-[#fd3eb5] hover:border-[#fd3eb5] transition-colors"
          style={{ fontSize: '12px', fontWeight: 600 }}
        >
          Ver todo →
        </a>
      </div>

      {/* Full-bleed 2-panel grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="grid lg:grid-cols-3"
        style={{ minHeight: '480px' }}
      >
        {/* Main event: Summer Camp — clickable */}
        <button
          className="lg:col-span-2 relative overflow-hidden group text-left"
          style={{ minHeight: '480px' }}
          onClick={() => go('summercamp')}
        >
          <img
            src={IMG_DANCE}
            alt="Kids Summer Camp"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            style={{ position: 'absolute', inset: 0 }}
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(160deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0.93) 100%)' }} />

          {/* Copy — vertically centered */}
          <div className="absolute inset-0 flex flex-col justify-center px-8 lg:px-14">
            <p style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#009edd', marginBottom: 12 }}>
              Vacaciones 2026 · Ocean Adventure
            </p>
            <h2 style={{ fontSize: 'clamp(44px, 6.5vw, 88px)', fontWeight: 900, letterSpacing: '-0.04em', textTransform: 'uppercase', lineHeight: 0.87, color: '#fff', marginBottom: 16 }}>
              KIDS<br />SUMMER<br />CAMP
            </h2>
            <p style={{ fontSize: '14px', lineHeight: 1.65, maxWidth: '340px', color: 'rgba(255,255,255,0.65)', marginBottom: 28 }}>
              Natación, patinaje, artes marciales, pintura, equitación y mucho más — aventura total en vacaciones.
            </p>
            <span
              className="inline-flex items-center gap-2 group-hover:gap-4 transition-all"
              style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#fff', background: '#001489', padding: '13px 28px', width: 'fit-content' }}
            >
              Ver el campamento →
            </span>
          </div>
        </button>

        {/* Side panel */}
        <div className="bg-[#001489] flex flex-col justify-between p-10 lg:p-12" style={{ minHeight: '280px' }}>
          <div>
            <p style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#009edd', marginBottom: 28 }}>
              Próximamente
            </p>
            <p style={{ fontSize: 'clamp(44px, 4.5vw, 68px)', fontWeight: 900, letterSpacing: '-0.04em', textTransform: 'uppercase', lineHeight: 0.88, color: 'rgba(255,255,255,0.12)' }}>
              ¿Tu<br />evento<br />aquí?
            </p>
          </div>
          <div>
            <p style={{ fontSize: '13px', lineHeight: 1.65, color: 'rgba(255,255,255,0.45)', marginBottom: 24 }}>
              Torneos, competencias y actividades especiales. Síguenos en Instagram para no perderte nada.
            </p>
            <a
              href="https://www.instagram.com/centrodeportivoluzmerytristan"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-white border-b border-white/20 pb-1 w-fit hover:border-[#fd3eb5] hover:text-[#fd3eb5] transition-colors"
              style={{ fontSize: '12px', fontWeight: 600 }}
            >
              @centrodep.luzmerytristan →
            </a>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

// ── Home › Testimonials ───────────────────────────────────────────────────────
const TESTIMONIALS = [
  { name: 'María Fernanda L.', role: 'Mamá — Patinaje', text: 'Mis hijos aman venir cada sábado. El ambiente es increíble y los profes tratan a todos como familia.' },
  { name: 'Carlos Muñoz', role: 'Papá — Natación', text: 'Empecé trayendo a mi hijo y terminé inscribiéndome yo también. Ahora nadamos juntos.' },
  { name: 'Ana Lucía V.', role: 'Mamá — Natación Adultos', text: 'Volver a nadar después de 20 años fue lo mejor que pude hacer. El equipo es muy profesional y cariñoso.' },
  { name: 'Roberto Caicedo', role: 'Abuelo — Patinaje', text: 'Traigo a mi nieto al patinaje mientras yo uso el gimnasio. Un plan perfecto para toda la familia.' },
];

function TestimonialsSection() {
  const [idx, setIdx] = useState(0);
  const t = TESTIMONIALS[idx]!;
  return (
    <section className="bg-white py-16 lg:py-20">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <p className="text-[#009edd] mb-2" style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
            Familias felices
          </p>
          <div className="flex justify-center gap-1 mb-8">
            {[1, 2, 3, 4, 5].map((s) => <Star key={s} size={15} className="text-[#fd3eb5] fill-[#fd3eb5]" />)}
          </div>
          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3 }}
            >
              <p className="text-[#001489] mb-5" style={{ fontSize: 'clamp(18px, 2.5vw, 24px)', lineHeight: 1.6, fontWeight: 500 }}>
                &ldquo;{t.text}&rdquo;
              </p>
              <p className="text-[#001489]" style={{ fontSize: '14px', fontWeight: 700 }}>{t.name}</p>
              <p className="text-gray-400 mt-1" style={{ fontSize: '13px' }}>{t.role}</p>
            </motion.div>
          </AnimatePresence>
          <div className="flex justify-center gap-2 mt-8">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className="rounded-full transition-all duration-200"
                style={{ width: i === idx ? 24 : 8, height: 8, background: i === idx ? '#001489' : '#e5e7eb' }}
                aria-label={`Testimonio ${i + 1}`}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ── Home › Contact CTA ────────────────────────────────────────────────────────
function HomeCTA() {
  return (
    <section className="bg-[#001489]">
      <div className="max-w-4xl mx-auto px-6 py-16 lg:py-24 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <p className="text-[#009edd] mb-3" style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase' }}>
            Inscripciones abiertas
          </p>
          <h2 className="text-white mb-4" style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: 900, lineHeight: 1, letterSpacing: '-0.03em', textTransform: 'uppercase' }}>
            ¡Ven con<br />tu Familia!
          </h2>
          <p className="text-white/60 mb-8 max-w-sm mx-auto" style={{ fontSize: '15px', lineHeight: 1.65 }}>
            Escríbenos por WhatsApp y agenda una clase de prueba gratis.
          </p>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1ebe57] text-white px-10 py-4 rounded-full transition-colors"
            style={{ fontSize: '16px', fontWeight: 700 }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" fill="currentColor" width="20" height="20" aria-hidden="true">
              <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" />
            </svg>
            Escribir por WhatsApp
          </a>
          <div className="grid sm:grid-cols-3 gap-6 mt-12">
            <div className="flex flex-col items-center">
              <MapPin size={18} className="text-[#009edd] mb-2" />
              <p className="text-white" style={{ fontSize: '14px', fontWeight: 600 }}>Cl. 18 #168-5149</p>
              <p className="text-white/50" style={{ fontSize: '13px' }}>Cali, Valle del Cauca</p>
            </div>
            <div className="flex flex-col items-center">
              <Phone size={18} className="text-[#009edd] mb-2" />
              <p className="text-white" style={{ fontSize: '14px', fontWeight: 600 }}>+57 315 550 2034</p>
              <p className="text-white/50" style={{ fontSize: '13px' }}>WhatsApp · Lun a Sáb</p>
            </div>
            <div className="flex flex-col items-center">
              <Clock size={18} className="text-[#009edd] mb-2" />
              <p className="text-white" style={{ fontSize: '14px', fontWeight: 600 }}>Lun – Sáb</p>
              <p className="text-white/50" style={{ fontSize: '13px' }}>6:00 a.m. – 9:00 p.m.</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ── Service › Pricing table ───────────────────────────────────────────────────
function PricingTable({ groups }: { groups: PlanGroup[] }) {
  const [active, setActive] = useState(0);
  const g = groups[active]!;
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6">
        {groups.map((gr, i) => (
          <button
            key={gr.title}
            onClick={() => setActive(i)}
            className="px-4 py-2 rounded-full transition-all"
            style={{
              fontSize: '13px',
              fontWeight: 600,
              background: active === i ? '#001489' : '#f0f6ff',
              color: active === i ? '#fff' : '#001489',
            }}
          >
            {gr.title}
          </button>
        ))}
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.22 }}
        >
          {g.matricula && (
            <p className="text-[#fd3eb5] mb-4" style={{ fontSize: '13px', fontWeight: 600 }}>
              Matrícula: {g.matricula}
            </p>
          )}
          <div className="divide-y divide-gray-100">
            {g.tiers.map((t) => (
              <div key={t.name} className="flex items-center justify-between py-3.5">
                <span className="text-gray-700" style={{ fontSize: '15px' }}>{t.name}</span>
                <span className="text-[#001489]" style={{ fontSize: '15px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{t.price}</span>
              </div>
            ))}
          </div>
          {g.schedule && g.schedule.length > 0 && (
            <div className="mt-5 p-4 rounded-lg bg-[#f0f6ff]">
              <div className="flex items-center gap-2 mb-2 text-[#009edd]">
                <Clock size={13} />
                <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Horarios</span>
              </div>
              {g.schedule.map((s) => (
                <p key={s} className="text-gray-600" style={{ fontSize: '13px', lineHeight: 1.7 }}>{s}</p>
              ))}
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// ── Service gallery (shared across all services) ──────────────────────────────
function ServiceGallery({ images }: { images: string[] }) {
  const [lightbox, setLightbox] = useState<number | null>(null);
  const total = images.length;

  useEffect(() => {
    if (lightbox === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null);
      else if (e.key === 'ArrowRight') setLightbox((p) => ((p ?? 0) + 1) % total);
      else if (e.key === 'ArrowLeft') setLightbox((p) => ((p ?? 0) - 1 + total) % total);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox, total]);

  if (!total) return null;

  return (
    <>
      {/* ── Header ── */}
      <div className="flex items-center justify-between pt-14 pb-6">
        <p style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#001489' }}>
          Galería
        </p>
        <span style={{ fontSize: '12px', color: '#9ca3af' }}>
          {total} {total === 1 ? 'foto' : 'fotos'}
        </span>
      </div>

      {/* ── Grid compacto: altura fija, 3 cols ── */}
      <div className="-mx-6 lg:-mx-16">
        <div
          className="grid grid-cols-2 lg:grid-cols-3"
          style={{ gap: '3px' }}
        >
          {images.map((src, i) => (
            <button
              key={i}
              className="group relative overflow-hidden"
              style={{ height: '200px' }}
              onClick={() => setLightbox(i)}
            >
              {isVideo(src) ? (
                <video
                  src={src}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-full object-cover pointer-events-none"
                />
              ) : (
                <img
                  src={src}
                  alt=""
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              )}
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end"
                style={{ background: 'linear-gradient(to top, rgba(0,20,137,0.6) 0%, transparent 55%)' }}
              >
                <span style={{ color: '#fff', fontSize: '11px', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', padding: '10px 12px' }}>
                  {isVideo(src) ? '▶ Ver' : 'Ver →'}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* ── Lightbox ── */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-[200] flex items-center justify-center"
            style={{ background: '#000' }}
            onClick={() => setLightbox(null)}
          >
            {/* Close */}
            <button
              className="absolute top-5 right-5 text-white/60 hover:text-white transition-colors z-10"
              onClick={() => setLightbox(null)}
              aria-label="Cerrar"
            >
              <X size={22} />
            </button>

            {/* Counter */}
            <span className="absolute top-6 left-6" style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', fontWeight: 600, letterSpacing: '0.1em' }}>
              {String(lightbox + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
            </span>

            {/* Image + arrows */}
            <div
              className="flex items-center w-full max-w-5xl px-4 lg:px-10"
              style={{ gap: 12 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setLightbox((lightbox - 1 + total) % total)}
                className="shrink-0 text-white/40 hover:text-white transition-colors p-2"
                aria-label="Anterior"
              >
                <ChevronLeft size={30} />
              </button>

              {isVideo(images[lightbox]) ? (
                <motion.video
                  key={lightbox}
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.18 }}
                  src={images[lightbox]}
                  muted
                  autoPlay
                  loop
                  playsInline
                  controls
                  className="flex-1 object-contain"
                  style={{ maxHeight: '86vh' }}
                />
              ) : (
                <motion.img
                  key={lightbox}
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.18 }}
                  src={images[lightbox]}
                  alt=""
                  className="flex-1 object-contain"
                  style={{ maxHeight: '86vh' }}
                />
              )}

              <button
                onClick={() => setLightbox((lightbox + 1) % total)}
                className="shrink-0 text-white/40 hover:text-white transition-colors p-2"
                aria-label="Siguiente"
              >
                <ChevronRight size={30} />
              </button>
            </div>

            {/* Dots */}
            <div
              className="absolute bottom-6 flex items-center"
              style={{ gap: 6 }}
              onClick={(e) => e.stopPropagation()}
            >
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setLightbox(i)}
                  style={{
                    height: 4,
                    width: i === lightbox ? 24 : 8,
                    borderRadius: 2,
                    background: i === lightbox ? '#fff' : 'rgba(255,255,255,0.22)',
                    transition: 'all 0.2s',
                  }}
                  aria-label={`Foto ${i + 1}`}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ── Service view ──────────────────────────────────────────────────────────────
function ServiceView({ service, go }: { service: ServiceDef; go: (v: View) => void }) {
  const groups = service.id === 'natacion' ? NATACION_GROUPS : service.id === 'patinaje' ? PATINAJE_GROUPS : null;
  const [tab, setTab] = useState<'precios' | 'sobre'>('precios');
  const others = CATALOG.filter((s) => s.id !== service.id).slice(0, 3);
  const gallery = GALLERIES[service.id] ?? [];

  return (
    <>
      {/* Hero */}
      <div className="relative w-full" style={{ height: 'min(62vh, 520px)', minHeight: '300px' }}>
        <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,20,137,0.92) 0%, rgba(0,20,137,0.4) 55%, transparent 100%)' }} />
        <div className="absolute bottom-0 left-0 right-0 px-8 lg:px-16 pb-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
            <p className="text-[#009edd] mb-2" style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase' }}>
              {service.subtitle}
            </p>
            <h1 className="text-white" style={{ fontSize: 'clamp(36px, 6vw, 72px)', fontWeight: 900, letterSpacing: '-0.03em', textTransform: 'uppercase', lineHeight: 1 }}>
              {service.title}
            </h1>
          </motion.div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-[1920px] mx-auto px-6 lg:px-16 py-12 lg:py-16">
        <div className="grid lg:grid-cols-3 gap-10 lg:gap-16">

          {/* Main */}
          <div className="lg:col-span-2">
            {groups ? (
              <>
                <div className="flex gap-3 mb-8">
                  {(['precios', 'sobre'] as const).map((t) => (
                    <button
                      key={t}
                      onClick={() => setTab(t)}
                      className="px-5 py-2 rounded-full transition-all"
                      style={{
                        fontSize: '14px',
                        fontWeight: 700,
                        background: tab === t ? '#001489' : 'transparent',
                        color: tab === t ? '#fff' : '#001489',
                        border: '2px solid',
                        borderColor: tab === t ? '#001489' : '#e5e7eb',
                      }}
                    >
                      {t === 'precios' ? 'Precios' : 'Sobre el programa'}
                    </button>
                  ))}
                </div>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={tab}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                  >
                    {tab === 'precios' ? (
                      <PricingTable groups={groups} />
                    ) : (
                      <div>
                        <p className="text-gray-600 mb-6" style={{ fontSize: '16px', lineHeight: 1.75 }}>{service.body}</p>
                        <div className="flex flex-wrap gap-2">
                          {service.tags.map((tag) => (
                            <span key={tag} className="px-3 py-1.5 rounded-full text-[#009edd] bg-[#f0f6ff]" style={{ fontSize: '12px', fontWeight: 600 }}>
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </>
            ) : (
              <div>
                <p className="text-gray-600 mb-6" style={{ fontSize: '16px', lineHeight: 1.75 }}>{service.body}</p>
                <div className="flex flex-wrap gap-2 mb-8">
                  {service.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1.5 rounded-full text-[#009edd] bg-[#f0f6ff]" style={{ fontSize: '12px', fontWeight: 600 }}>
                      {tag}
                    </span>
                  ))}
                </div>
                <p className="text-gray-400" style={{ fontSize: '14px' }}>
                  Para conocer precios y disponibilidad, contáctanos directamente.
                </p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div>
            <div className="sticky top-24 bg-[#001489] rounded-xl p-6 text-white">
              <p className="text-[#009edd] mb-1" style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                ¿Listo para empezar?
              </p>
              <h3 className="text-white mb-3" style={{ fontSize: '20px', fontWeight: 800, lineHeight: 1.1 }}>
                Agenda tu clase gratis
              </h3>
              <p className="text-white/60 mb-5" style={{ fontSize: '14px', lineHeight: 1.6 }}>
                Escríbenos por WhatsApp y te ayudamos a elegir el programa ideal.
              </p>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center py-3.5 rounded-full text-white transition-opacity hover:opacity-90"
                style={{ fontSize: '15px', fontWeight: 700, background: '#25D366' }}
              >
                Inscribirme por WhatsApp
              </a>
              <div className="mt-5 pt-5 border-t border-white/10 space-y-2.5">
                <div className="flex items-center gap-2 text-white/70">
                  <MapPin size={13} className="text-[#009edd] shrink-0" />
                  <span style={{ fontSize: '13px' }}>Cl. 18 #168-5149, Cali</span>
                </div>
                <div className="flex items-center gap-2 text-white/70">
                  <Phone size={13} className="text-[#009edd] shrink-0" />
                  <span style={{ fontSize: '13px' }}>+57 315 550 2034</span>
                </div>
                <div className="flex items-center gap-2 text-white/70">
                  <Clock size={13} className="text-[#009edd] shrink-0" />
                  <span style={{ fontSize: '13px' }}>Lun – Sáb · 6am – 9pm</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {gallery.length > 0 && <ServiceGallery images={gallery} />}

        {/* Other services */}
        <div className="mt-14 pt-10 border-t border-gray-100">
          <p className="text-[#001489] mb-5" style={{ fontSize: '15px', fontWeight: 700 }}>Otros servicios</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {others.map((s) => (
              <button
                key={s.id}
                onClick={() => go(s.id)}
                className="group flex items-center gap-3 p-3 rounded-xl hover:bg-[#f0f6ff] transition-colors text-left"
              >
                <img src={s.image} alt={s.title} className="w-14 h-14 rounded-lg object-cover shrink-0" />
                <div>
                  <p className="text-[#001489] group-hover:text-[#009edd] transition-colors" style={{ fontSize: '14px', fontWeight: 700 }}>{s.title}</p>
                  <p className="text-gray-400" style={{ fontSize: '12px' }}>{s.subtitle}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

// ── Summer Camp view ──────────────────────────────────────────────────────────
function SummerCampView({ go }: { go: (v: View) => void }) {
  const [lightbox, setLightbox] = useState<number | null>(null);
  const total = SC_GALLERY.length;

  useEffect(() => {
    if (lightbox === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null);
      else if (e.key === 'ArrowRight') setLightbox((p) => ((p ?? 0) + 1) % total);
      else if (e.key === 'ArrowLeft') setLightbox((p) => ((p ?? 0) - 1 + total) % total);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox, total]);

  return (
    <>
      {/* Hero */}
      <section className="relative w-full overflow-hidden" style={{ height: 'clamp(400px, 52vw, 600px)' }}>
        <img src={`${M}/sc-hero.jpg`} alt="Kids Summer Camp" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(160deg, rgba(0,20,137,0.82) 0%, rgba(0,20,137,0.45) 50%, rgba(0,20,137,0.1) 100%)' }} />
        <div className="absolute inset-0 flex items-center px-8 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
            <span style={{ display: 'inline-block', fontSize: '10px', fontWeight: 700, letterSpacing: '0.25em', textTransform: 'uppercase', background: '#fd3eb5', color: '#fff', padding: '4px 12px', marginBottom: 16 }}>
              Vacaciones 2026
            </span>
            <h1 style={{ fontSize: 'clamp(48px, 9vw, 112px)', fontWeight: 900, letterSpacing: '-0.04em', textTransform: 'uppercase', lineHeight: 0.84, color: '#fff', marginBottom: 16 }}>
              KIDS<br />SUMMER<br /><span style={{ color: '#009edd' }}>CAMP</span>
            </h1>
            <p style={{ fontSize: 'clamp(13px, 1.5vw, 17px)', color: 'rgba(255,255,255,0.75)', maxWidth: '380px', lineHeight: 1.65 }}>
              Una semana de aventura, deporte y creatividad para niños de 4 a 14 años.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-white">
        <div className="max-w-5xl mx-auto px-8 lg:px-16 py-14 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#009edd', marginBottom: 12 }}>Ocean Adventure</p>
              <h2 style={{ fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1, color: '#001489', textTransform: 'uppercase' }}>
                La aventura de<br />vacaciones más<br />completa de Cali.
              </h2>
            </div>
            <div className="space-y-4">
              <p style={{ fontSize: '15px', lineHeight: 1.8, color: '#4b5563' }}>
                El Kids Summer Camp del Centro Deportivo Luz Mery Tristán es mucho más que un campamento: es una experiencia total donde cada día trae una actividad diferente, siempre en un ambiente seguro, divertido y lleno de energía.
              </p>
              <p style={{ fontSize: '15px', lineHeight: 1.8, color: '#4b5563' }}>
                Instructores certificados, instalaciones de primer nivel y un programa diseñado para que los niños exploren, aprendan y hagan amigos.
              </p>
              <a
                href="https://wa.me/573155940251?text=Hola%2C%20quiero%20informaci%C3%B3n%20sobre%20el%20Kids%20Summer%20Camp"
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: '13px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', background: '#001489', color: '#fff', padding: '13px 28px', marginTop: 8 }}
                className="hover:bg-[#009edd] transition-colors"
              >
                ¡Inscríbete ya! →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Activities — open grid, no dividers */}
      <section style={{ background: '#fff' }}>
        <div className="max-w-5xl mx-auto px-8 lg:px-16 pt-16 pb-16">
          <p style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.26em', textTransform: 'uppercase', color: '#009edd', marginBottom: 10 }}>Actividades</p>
          <h2 style={{ fontSize: 'clamp(22px, 3.5vw, 36px)', fontWeight: 900, color: '#001489', textTransform: 'uppercase', letterSpacing: '-0.03em', lineHeight: 1, marginBottom: 56 }}>
            Cada día,<br />una aventura distinta.
          </h2>

          <div className="grid grid-cols-2 lg:grid-cols-4" style={{ rowGap: '40px', columnGap: '32px' }}>
            {SC_GALLERY.slice(0, 8).map((item, i) => (
              <div key={i}>
                <span style={{ display: 'block', fontSize: 'clamp(42px, 5vw, 60px)', fontWeight: 900, color: '#e4ecf9', letterSpacing: '-0.05em', lineHeight: 1, marginBottom: 18 }}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <p style={{ fontSize: '11px', fontWeight: 700, color: '#001489', textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 8 }}>{item.label}</p>
                <p style={{ fontSize: '13px', color: '#94a3b8', lineHeight: 1.7 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section style={{ background: '#06060e' }}>
        <div className="px-8 lg:px-16 pt-12 pb-5 flex items-end justify-between">
          <div>
            <p style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.28em', textTransform: 'uppercase', color: '#009edd', marginBottom: 6 }}>Galería</p>
            <h3 style={{ fontSize: 'clamp(22px, 3vw, 32px)', fontWeight: 900, color: '#fff', textTransform: 'uppercase', letterSpacing: '-0.03em', lineHeight: 1 }}>
              Momentos del camp
            </h3>
          </div>
          <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.22)', letterSpacing: '0.08em' }}>{SC_GALLERY.length} fotos &amp; videos</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3" style={{ gap: '3px' }}>
          {SC_GALLERY.map((item, i) => {
            const tall = i === 0 || i === 9;
            return (
              <button
                key={i}
                className={`group relative overflow-hidden${tall ? ' row-span-2' : ''}`}
                style={{ height: tall ? 'clamp(320px, 40vw, 520px)' : 'clamp(170px, 20vw, 270px)' }}
                onClick={() => setLightbox(i)}
              >
                {isVideo(item.src) ? (
                  <video src={item.src} autoPlay muted loop playsInline className="w-full h-full object-cover pointer-events-none transition-transform duration-500 group-hover:scale-105" />
                ) : (
                  <img src={item.src} alt={item.label} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                )}
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.68) 0%, transparent 52%)' }} />
                <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between px-4 pb-4">
                  <span style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.85)' }}>
                    {item.label}
                  </span>
                  {isVideo(item.src) && (
                    <span style={{ fontSize: '9px', fontWeight: 700, color: '#fd3eb5', letterSpacing: '0.1em' }}>▶ VIDEO</span>
                  )}
                </div>
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: 'rgba(0,20,137,0.2)' }} />
              </button>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-14 px-8 lg:px-16 text-center">
        <p style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#009edd', marginBottom: 12 }}>
          Cupos limitados
        </p>
        <h2 style={{ fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 900, color: '#001489', textTransform: 'uppercase', letterSpacing: '-0.03em', lineHeight: 1, marginBottom: 16 }}>
          ¿Tu hijo en el<br />próximo camp?
        </h2>
        <p style={{ fontSize: '15px', color: '#6b7280', lineHeight: 1.65, maxWidth: '380px', margin: '0 auto 28px' }}>
          Escríbenos por WhatsApp y te enviamos toda la información, fechas y precios.
        </p>
        <a
          href="https://wa.me/573155940251?text=Hola%2C%20quiero%20inscribirme%20en%20el%20Kids%20Summer%20Camp"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-3 text-white px-10 py-4 rounded-full hover:bg-[#1ebe57] transition-colors"
          style={{ fontSize: '15px', fontWeight: 700, background: '#25D366' }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" fill="currentColor" width="18" height="18" aria-hidden="true"><path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/></svg>
          Inscribirse por WhatsApp
        </a>
        <div className="mt-10">
          <button onClick={() => go('home')} style={{ fontSize: '13px', color: '#9ca3af' }} className="hover:text-[#001489] transition-colors">
            ← Volver al inicio
          </button>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-[200] flex items-center justify-center"
            style={{ background: '#000' }}
            onClick={() => setLightbox(null)}
          >
            <button className="absolute top-5 right-5 text-white/60 hover:text-white transition-colors z-10" onClick={() => setLightbox(null)}><X size={22} /></button>
            <span className="absolute top-6 left-6" style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', fontWeight: 600, letterSpacing: '0.1em' }}>
              {String(lightbox + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
            </span>
            <div className="flex items-center w-full max-w-5xl px-4 lg:px-10" style={{ gap: 12 }} onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setLightbox((lightbox - 1 + total) % total)} className="shrink-0 text-white/40 hover:text-white transition-colors p-2"><ChevronLeft size={30} /></button>
              {isVideo(SC_GALLERY[lightbox].src) ? (
                <motion.video key={lightbox} initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.18 }} src={SC_GALLERY[lightbox].src} muted autoPlay loop playsInline controls className="flex-1 object-contain" style={{ maxHeight: '86vh' }} />
              ) : (
                <motion.img key={lightbox} initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.18 }} src={SC_GALLERY[lightbox].src} alt="" className="flex-1 object-contain" style={{ maxHeight: '86vh' }} />
              )}
              <button onClick={() => setLightbox((lightbox + 1) % total)} className="shrink-0 text-white/40 hover:text-white transition-colors p-2"><ChevronRight size={30} /></button>
            </div>
            <div className="absolute bottom-6 flex items-center" style={{ gap: 6 }} onClick={(e) => e.stopPropagation()}>
              {SC_GALLERY.map((_, i) => (
                <button key={i} onClick={() => setLightbox(i)} style={{ height: 4, width: i === lightbox ? 24 : 8, borderRadius: 2, background: i === lightbox ? '#fff' : 'rgba(255,255,255,0.22)', transition: 'all 0.2s' }} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer({ go }: { go: (v: View) => void }) {
  return (
    <footer className="bg-[#001489] text-white">
      <div className="max-w-[1920px] mx-auto px-6 lg:px-10 pt-12 pb-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
          <div>
            <h4 className="text-white mb-4" style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
              Servicios
            </h4>
            <ul className="space-y-2">
              {CATALOG.map((s) => (
                <li key={s.id}>
                  <button
                    onClick={() => go(s.id)}
                    className="text-gray-400 hover:text-white transition-colors"
                    style={{ fontSize: '13px' }}
                  >
                    {s.title}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-white mb-4" style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
              Centro
            </h4>
            <ul className="space-y-2">
              {['Nuestra Historia', 'Instalaciones', 'Horarios', 'Tarifas'].map((item) => (
                <li key={item}>
                  <span className="text-gray-400 hover:text-white transition-colors cursor-pointer" style={{ fontSize: '13px' }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-white mb-4" style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
              Ayuda
            </h4>
            <ul className="space-y-2">
              {['Preguntas Frecuentes', 'Cancelaciones', 'Contáctanos'].map((item) => (
                <li key={item}>
                  <span className="text-gray-400 hover:text-white transition-colors cursor-pointer" style={{ fontSize: '13px' }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-white mb-4" style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
              Síguenos
            </h4>
            <ul className="space-y-2">
              {['Instagram', 'Facebook', 'YouTube'].map((item) => (
                <li key={item}>
                  <span className="text-gray-400 hover:text-white transition-colors cursor-pointer" style={{ fontSize: '13px' }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <img src={LOGO} alt="Centro Deportivo LMT" className="h-7 w-auto" style={{ filter: 'brightness(0) invert(1)', opacity: 0.7 }} />
          <p className="text-gray-600" style={{ fontSize: '12px' }}>
            © 2026 Centro Deportivo Luz Mery Tristán. Todos los derechos reservados.
          </p>
          <a href="https://plada.ai" target="_blank" rel="noopener noreferrer" className="plada-footer-mark text-gray-600 hover:text-white transition-colors" style={{ fontSize: '12px' }}>
            Built with Plada
          </a>
        </div>
      </div>
    </footer>
  );
}

// ── WhatsApp FAB ──────────────────────────────────────────────────────────────
function WhatsAppFAB() {
  return (
    <a
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-5 py-3 rounded-full text-white shadow-2xl hover:scale-105 transition-transform"
      style={{ background: '#25D366', fontSize: '14px', fontWeight: 600 }}
      aria-label="WhatsApp"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" fill="currentColor" width="18" height="18" aria-hidden="true">
        <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" />
      </svg>
      <span className="hidden sm:inline">¿Necesitas ayuda?</span>
    </a>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function CentroDeportivoLmt() {
  const { view, go } = useNav();
  const service = view !== 'lmtristan' ? (CATALOG.find((s) => s.id === view) ?? null) : null;

  useEffect(() => {
    scrollTop();
  }, [view]);

  useEffect(() => {
    document.title = service
      ? `${service.title} — Centro Deportivo Luz Mery Tristán`
      : 'Centro Deportivo Luz Mery Tristán';
    const existing = document.head.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]');
    existing.forEach((el) => el.parentNode?.removeChild(el));
    const li = document.createElement('link');
    li.rel = 'icon'; li.type = 'image/png'; li.media = '(prefers-color-scheme: light)'; li.href = FAVICON_LIGHT;
    document.head.appendChild(li);
    const di = document.createElement('link');
    di.rel = 'icon'; di.type = 'image/png'; di.media = '(prefers-color-scheme: dark)'; di.href = FAVICON_DARK;
    document.head.appendChild(di);
    const ai = document.createElement('link');
    ai.rel = 'apple-touch-icon'; ai.href = FAVICON_LIGHT;
    document.head.appendChild(ai);
  }, [view, service]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar view={view} go={go} />
      <AnimatePresence mode="wait">
        <motion.div
          key={view}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
        >
          {view === 'lmtristan' ? (
            <LuzMeryView go={go} />
          ) : view === 'summercamp' ? (
            <SummerCampView go={go} />
          ) : view === 'home' || !service ? (
            <>
              <HeroSection go={go} />
              <StatsBar />
              <ServicesGrid go={go} />
              <AboutSection />
              <LuzMeryTeaser go={go} />
              <EventsSection go={go} />
              <TestimonialsSection />
              <HomeCTA />
            </>
          ) : (
            <ServiceView service={service} go={go} />
          )}
        </motion.div>
      </AnimatePresence>
      <Footer go={go} />
      <WhatsAppFAB />
    </div>
  );
}
